import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Key, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Settings,
  User,
  Save,
  ExternalLink,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apsConfig, setApsConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [apsAuthStatus, setApsAuthStatus] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);

  useEffect(() => {
    loadApsConfig();
    loadApsAuthStatus();
  }, []);

  const loadApsConfig = async () => {
    try {
      const response = await axios.get('/settings/aps-credentials');
      setApsConfig(response.data);
    } catch (err) {
      console.error('Failed to load APS config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadApsAuthStatus = async () => {
    try {
      const response = await axios.get('/auth/aps/status');
      setApsAuthStatus(response.data);
    } catch (err) {
      console.error('Failed to load APS auth status:', err);
      setApsAuthStatus({ authorized: false });
    }
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post('/settings/aps-credentials', {
        client_id: clientId,
        client_secret: clientSecret
      });

      if (response.data.success) {
        toast.success('APS credentials saved successfully');
        setClientId('');
        setClientSecret('');
        loadApsConfig(); // Refresh config
        loadApsAuthStatus(); // Refresh auth status
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save credentials';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthorizeAPS = async () => {
    setLoadingAuth(true);
    try {
      const response = await axios.get('/auth/aps/authorize');
      const { authorization_url } = response.data;
      
      // Redirect to Autodesk OAuth page
      window.location.href = authorization_url;
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to get authorization URL';
      toast.error(errorMsg);
      setLoadingAuth(false);
    }
  };

  if (loadingConfig) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <Button 
                onClick={() => navigate('/')} 
                variant="ghost" 
                size="sm"
                data-testid="back-to-dashboard"
                className="btn-ghost"
              >
                <ArrowLeft className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">DASHBOARD</span>
              </Button>
              <div className="w-px h-6 bg-slate-300"></div>
              <h1 className="technical-header text-lg md:text-xl flex items-center gap-2">
                <Settings className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">SETTINGS</span>
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="grid gap-6 md:gap-8">
          {/* User Profile Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="technical-header text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                USER PROFILE
              </CardTitle>
              <CardDescription className="technical-subtext">
                Your account information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">FULL NAME</Label>
                    <div className="input-field bg-slate-100 text-slate-600 cursor-not-allowed">
                      {user?.full_name || 'Not provided'}
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">EMAIL ADDRESS</Label>
                    <div className="input-field bg-slate-100 text-slate-600 cursor-not-allowed">
                      {user?.email}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Account created: {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* APS OAuth Authorization Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="technical-header text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                APS AUTHORIZATION
              </CardTitle>
              <CardDescription className="technical-subtext">
                Authorize access to your Autodesk account to browse ACC/BIM 360 projects
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Authorization Status */}
              <div className="mb-6 p-4 bg-slate-100 border border-slate-200 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-800 text-sm">Authorization Status</h4>
                  {apsAuthStatus?.authorized ? (
                    <Badge className="badge-success">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      AUTHORIZED
                    </Badge>
                  ) : (
                    <Badge className="badge-failed">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      NOT AUTHORIZED
                    </Badge>
                  )}
                </div>
                {apsAuthStatus?.authorized ? (
                  <div className="space-y-1">
                    <div className="text-sm text-slate-600">
                      ✅ You can access your ACC/BIM 360 projects
                    </div>
                    {apsAuthStatus.expires_at && (
                      <div className="text-xs text-slate-500">
                        Token expires: {new Date(apsAuthStatus.expires_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600">
                    ⚠️ Authorization required to browse your projects and generate model URLs
                  </div>
                )}
              </div>

              {/* Authorization Action */}
              {!apsAuthStatus?.authorized ? (
                <div>
                  {apsConfig?.configured ? (
                    <div>
                      <Button
                        onClick={handleAuthorizeAPS}
                        disabled={loadingAuth}
                        className="btn-primary mb-4"
                        data-testid="authorize-aps-button"
                      >
                        {loadingAuth ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            REDIRECTING TO AUTODESK...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            AUTHORIZE WITH AUTODESK
                          </>
                        )}
                      </Button>
                      
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                        <h5 className="font-semibold text-blue-800 text-sm mb-2">What happens next?</h5>
                        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                          <li>You'll be redirected to Autodesk's secure login page</li>
                          <li>Sign in with your Autodesk/ACC account</li>
                          <li>Grant permission to access your projects</li>
                          <li>You'll be redirected back to continue</li>
                        </ol>
                      </div>
                    </div>
                  ) : (
                    <Alert className="border-orange-200 bg-orange-50">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-800">
                        Please save your APS credentials first, then you can authorize access to your Autodesk account.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
                    <p className="text-sm text-green-800 mb-2">
                      🎉 You're all set! You can now browse your ACC/BIM 360 projects.
                    </p>
                    <Button
                      onClick={() => navigate('/')}
                      className="btn-primary text-sm"
                      data-testid="go-to-dashboard"
                    >
                      GO TO DASHBOARD
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* APS Credentials Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="technical-header text-lg flex items-center gap-2">
                <Key className="w-5 h-5" />
                APS CREDENTIALS
              </CardTitle>
              <CardDescription className="technical-subtext">
                Configure your Autodesk Platform Services credentials to access ACC/BIM 360 projects
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Current Status */}
              {apsConfig && (
                <div className="mb-6 p-4 bg-slate-100 border border-slate-200 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-800 text-sm">Current Configuration</h4>
                    {apsConfig.configured ? (
                      <Badge className="badge-success">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        CONFIGURED
                      </Badge>
                    ) : (
                      <Badge className="badge-failed">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        NOT CONFIGURED
                      </Badge>
                    )}
                  </div>
                  {apsConfig.configured && (
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-slate-600">Client ID:</span>
                        <span className="mono text-slate-800 ml-2">{apsConfig.client_id}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        Last updated: {new Date(apsConfig.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Credentials Form */}
              <form onSubmit={handleSaveCredentials} className="space-y-6">
                <div className="grid gap-6">
                  <div className="form-group">
                    <Label htmlFor="clientId" className="form-label">
                      <Key className="w-4 h-4 inline mr-2" />
                      CLIENT ID
                    </Label>
                    <Input
                      id="clientId"
                      data-testid="client-id-input"
                      type="text"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      className="input-field font-mono text-sm"
                      placeholder="Enter your APS Client ID"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="clientSecret" className="form-label">
                      <Key className="w-4 h-4 inline mr-2" />
                      CLIENT SECRET
                    </Label>
                    <Input
                      id="clientSecret"
                      data-testid="client-secret-input"
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      className="input-field font-mono text-sm"
                      placeholder="Enter your APS Client Secret"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive" className="fade-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  data-testid="save-credentials-button"
                  disabled={isLoading || !clientId || !clientSecret}
                  className="btn-primary"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      VALIDATING & SAVING...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      SAVE CREDENTIALS
                    </>
                  )}
                </Button>
              </form>

              {/* Help Section */}
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">How to Get APS Credentials</h4>
                <div className="text-sm text-blue-700 space-y-2">
                  <p>1. Visit <a href="https://developer.autodesk.com" className="underline" target="_blank" rel="noopener noreferrer">developer.autodesk.com</a></p>
                  <p>2. Sign in and go to "My Apps"</p>
                  <p>3. Create a new app with "Server-to-Server" type</p>
                  <p>4. Copy the Client ID and Client Secret from your app</p>
                </div>
              </div>

              {/* Test Credentials Info */}
              <div className="mt-6 p-4 bg-slate-100 border border-slate-200 rounded-sm">
                <h4 className="font-semibold text-slate-800 mb-2 text-sm">Test Credentials Available</h4>
                <div className="mono-text text-xs space-y-1">
                  <div className="text-slate-600">Client ID: cDpDgcBwIUHh8XGaTaUmPdDI8CtsT53zKtl9czSG6AyZ1fIc</div>
                  <div className="text-slate-600">Client Secret: urfL0WckzgTt0g2FcJe9qcDZASXdxB7e5rxwnjHI1YpwJSwbt0hCM5uAckZYaHlN</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* About Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="technical-header text-lg flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                ABOUT THIS APPLICATION
              </CardTitle>
              <CardDescription className="technical-subtext">
                Engineered with clarity and first-principles thinking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Built By</h4>
                  <p className="text-sm text-slate-600 mb-2">
                    This application was engineered by{' '}
                    <a 
                      href="https://thedevguys.in" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600 font-medium underline transition-colors"
                    >
                      The Dev Guys
                    </a>
                    , an AI-enabled product engineering studio focused on building complex systems with clarity and speed.
                  </p>
                </div>
                
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Our Approach</h4>
                  <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
                    <li>First-principles thinking for clean architecture</li>
                    <li>Velocity through deep understanding</li>
                    <li>Empathy as an engineering skill</li>
                    <li>Clarity over noise in every decision</li>
                  </ul>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">Contact</h4>
                  <p className="text-sm text-slate-600">
                    For complex product engineering needs:{' '}
                    <a 
                      href="mailto:hello@thedevguys.in" 
                      className="text-orange-500 hover:text-orange-600 font-medium underline transition-colors"
                    >
                      hello@thedevguys.in
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}